import { useState, useCallback, useEffect, useRef } from "react";
import { _Api } from "../api/_call";
import { toast } from "react-toastify";

const SYNC_PRODUCT_URL = "api/v1/App/sync/catalog/product";
const EMBEDDING_BUTTON_COOLDOWN_MS = 10000;
export const CATALOG_EMBEDDING_NOTIFICATION_EVENT = "catalog-embedding-notification";

export const isCatalogEmbeddingNotification = (notification) => {
  const type =
    notification?.type ||
    notification?.notification_type ||
    notification?.slug ||
    notification?.module;
  return type === "catalog_embedding";
};

export const dispatchCatalogEmbeddingNotification = () => {
  window.dispatchEvent(new CustomEvent(CATALOG_EMBEDDING_NOTIFICATION_EVENT));
};

const formatRunningConflictMessage = (res, t) => {
  const progress = res?.job?.progress;
  if (progress?.percentage != null && progress?.total != null) {
    return t(
      "A sync is already running — {{percent}}% complete ({{completed}} of {{total}}). Try again once it finishes.",
      {
        percent: progress.percentage,
        completed: progress.completed ?? 0,
        total: progress.total,
      }
    );
  }
  return (
    res?.message ||
    t("A sync is already running. Try again once it finishes.")
  );
};

const formatJobStartedMessage = (res, t) => {
  if (res?.toEmbed != null && res.toEmbed > 0) {
    return t("Embedding sync started — {{count}} products will be embedded.", {
      count: res.toEmbed,
    });
  }
  if (res?.total != null && res.total > 0) {
    return t("Embedding sync started — {{count}} products will be embedded.", {
      count: res.total,
    });
  }
  return res?.message || t("Embedding sync started.");
};

export function useCatalogEmbeddingSync({ onComplete, t }) {
  const [isEmbeddingSubmitting, setIsEmbeddingSubmitting] = useState(false);
  const [isEmbeddingCooldown, setIsEmbeddingCooldown] = useState(false);
  const cooldownTimerRef = useRef(null);

  const startEmbeddingCooldown = useCallback(() => {
    setIsEmbeddingCooldown(true);
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
    }
    cooldownTimerRef.current = setTimeout(() => {
      setIsEmbeddingCooldown(false);
      cooldownTimerRef.current = null;
    }, EMBEDDING_BUTTON_COOLDOWN_MS);
  }, []);

  const handleSyncResponse = useCallback(
    (res) => {
      if (!res) return false;

      if (res.code === 409 && res.running) {
        toast.error(formatRunningConflictMessage(res, t));
        return false;
      }

      if (res.code === 400) {
        toast.error(res.message || t("Invalid selection"));
        return false;
      }

      if (res.code === 404) {
        toast.error(res.message || t("Products not found"));
        return false;
      }

      if (res.code === 500) {
        toast.error(res.message || t("Embedding sync failed"));
        return false;
      }

      if (
        res.code === 200 &&
        res.success &&
        !res.jobId &&
        (res.total === 0 || res.toEmbed === 0)
      ) {
        toast.info(res.message || t("All products are already synced"));
        return false;
      }

      if (
        (res.code === 202 || res.code === 200) &&
        res.success &&
        res.jobId
      ) {
        toast.success(formatJobStartedMessage(res, t));
        return true;
      }

      return false;
    },
    [t]
  );

  const runSync = useCallback(
    async (body) => {
      if (isEmbeddingSubmitting || isEmbeddingCooldown) return;
      setIsEmbeddingSubmitting(true);
      try {
        const res = await _Api(body, SYNC_PRODUCT_URL);
        const jobStarted = handleSyncResponse(res);
        if (jobStarted) {
          startEmbeddingCooldown();
        }
      } catch (err) {
        console.error("Failed to start catalog embedding sync:", err);
        toast.error(t("Embedding sync failed"));
      } finally {
        setIsEmbeddingSubmitting(false);
      }
    },
    [
      handleSyncResponse,
      isEmbeddingCooldown,
      isEmbeddingSubmitting,
      startEmbeddingCooldown,
      t,
    ]
  );

  const startSyncAll = useCallback(async () => {
    await runSync({});
  }, [runSync]);

  const startSyncProducts = useCallback(
    async (productIds) => {
      if (!productIds?.length) {
        toast.error(t("Select at least one product"));
        return;
      }
      await runSync({ product_id: productIds });
    },
    [runSync, t]
  );

  useEffect(() => {
    const onEmbeddingNotification = () => {
      onComplete?.();
    };

    window.addEventListener(
      CATALOG_EMBEDDING_NOTIFICATION_EVENT,
      onEmbeddingNotification
    );

    return () => {
      window.removeEventListener(
        CATALOG_EMBEDDING_NOTIFICATION_EVENT,
        onEmbeddingNotification
      );
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
  }, [onComplete]);

  const isEmbeddingButtonDisabled = isEmbeddingSubmitting || isEmbeddingCooldown;

  return {
    isEmbeddingSubmitting: isEmbeddingButtonDisabled,
    startSyncAll,
    startSyncProducts,
  };
}
